"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Trash2, MessageSquare, X } from "lucide-react";

interface Conversation {
  id: number;
  title: string;
  created_at: string;
}

interface ChatSidebarProps {
  currentId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  refreshKey: number;
  onClose?: () => void;
  onDeleted?: (id: number) => void;
}

const API_BASE = "/api/v1";

export function ChatSidebar({
  currentId,
  onSelect,
  onNew,
  refreshKey,
  onClose,
  onDeleted,
}: ChatSidebarProps) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fetchList = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/chat/conversations`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch {
      // 무시
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  const doDelete = useCallback(
    async (id: number) => {
      setDeletingId(id);
      try {
        const res = await fetch(`${API_BASE}/chat/conversations/${id}`, {
          method: "DELETE",
          credentials: "include",
        });
        if (res.ok) {
          // 즉시 UI에서 제거
          setConversations((prev) => prev.filter((c) => c.id !== id));
          setConfirmDeleteId(null);
          if (currentId === id) {
            onNew();
          }
          onDeleted?.(id);
        }
      } catch {
        // 무시
      } finally {
        setDeletingId(null);
      }
    },
    [currentId, onNew, onDeleted],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent, id: number) => {
      e.stopPropagation();
      e.preventDefault();
      if (confirmDeleteId === id) {
        doDelete(id);
      } else {
        setConfirmDeleteId(id);
        // 3초 후 확인 상태 초기화
        setTimeout(() => setConfirmDeleteId((prev) => (prev === id ? null : prev)), 3000);
      }
    },
    [confirmDeleteId, doDelete],
  );

  // 모바일: 길게 누르면 삭제 버튼 표시
  const handleTouchStart = useCallback((id: number) => {
    longPressTimer.current = setTimeout(() => {
      setHoveredId(id);
    }, 500);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  }, []);

  // confirmDeleteId 외부 클릭 시 초기화
  useEffect(() => {
    if (confirmDeleteId === null) return;
    const handler = () => setConfirmDeleteId(null);
    const timer = setTimeout(
      () => document.addEventListener("click", handler, { once: true }),
      100,
    );
    return () => clearTimeout(timer);
  }, [confirmDeleteId]);

  return (
    <div className="flex h-full flex-col border-r border-gray-200 bg-gray-50">
      {/* 헤더 */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-800">대화 목록</h2>
        <div className="flex items-center gap-1">
          <button
            onClick={onNew}
            className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-violet-700"
          >
            <Plus className="h-3.5 w-3.5" />새 대화
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="ml-1 flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 lg:hidden"
              aria-label="닫기"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* 대화 목록 */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">아직 대화가 없습니다</p>
        ) : (
          <ul className="py-1">
            {conversations.map((conv) => {
              const showDelete = hoveredId === conv.id || currentId === conv.id;
              const isConfirming = confirmDeleteId === conv.id;
              const isDeleting = deletingId === conv.id;
              return (
                <li
                  key={conv.id}
                  onClick={() => onSelect(conv.id)}
                  onMouseEnter={() => setHoveredId(conv.id)}
                  onMouseLeave={() => {
                    setHoveredId(null);
                    setConfirmDeleteId(null);
                  }}
                  onTouchStart={() => handleTouchStart(conv.id)}
                  onTouchEnd={handleTouchEnd}
                  className={`group flex cursor-pointer items-center gap-2.5 px-4 py-3 transition-colors ${
                    isDeleting ? "opacity-50" : ""
                  } ${
                    currentId === conv.id
                      ? "border-r-2 border-violet-500 bg-violet-50"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <MessageSquare className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="flex-1 truncate text-sm text-gray-700">
                    {conv.title || "새 대화"}
                  </span>
                  {showDelete && (
                    <button
                      onClick={(e) => handleDeleteClick(e, conv.id)}
                      disabled={isDeleting}
                      className={`flex shrink-0 items-center justify-center rounded transition-colors ${
                        isConfirming
                          ? "h-6 gap-1 bg-red-500 px-2 text-[10px] font-medium text-white"
                          : "h-6 w-6 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      }`}
                      aria-label={isConfirming ? "삭제 확인" : "삭제"}
                    >
                      {isConfirming ? (
                        isDeleting ? (
                          "..."
                        ) : (
                          "삭제"
                        )
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
